// =============================================================================
// domain/practice.ts — what a practice session adds up to. #59.
// -----------------------------------------------------------------------------
// ── ⚠ THE SESSION IS ONE IMPORT, AND ITS DATE IS A LABEL ──
//
// `docs/PRACTICE_PLAN.md` is explicit and has been re-derived wrong five times,
// so it is worth repeating where the code can see it: Practice does not
// DISCOVER sessions. It does not scan, group or infer. A session is one import
// of the trades the user picked, and `marketDate` is what that import happened
// to cover — not an identity, and not something to group by.
//
// ── 🚨 A PRACTICE TRADE IS A REPLAY. IT IS NOT MONEY. ──
//
// These P&L figures are what the trader WOULD have made. Every surface that
// shows them has to keep that distinction visible, because the numbers are
// shaped exactly like real ones and sit in an app whose other screens are real.
// The old client's coach prompt makes the same point to the model — it is told
// not to treat replay P&L as real money.
// =============================================================================

/**
 * The subset of a copied trade this module needs.
 *
 * ⚠ Deliberately narrow. The stored object carries the WHOLE trade (see
 * `io/sync/practice.ts`), and reaching into more of it from here would tie the
 * session summary to a shape that exists to be forwarded, not read.
 */
export interface PracticeTradeLike {
  netPnl?: number | null;
  patternId?: string | null;
  grade?: string | null;
  notes?: string | null;
}

export interface PracticeSessionSummary {
  /** How many trades were copied in. */
  n: number;
  /** 🚨 Replay P&L — what they WOULD have made. Never presented as money earned. */
  net: number;
  wins: number;
  losses: number;
  /** Percent over decided trades, or null when none are decided. */
  winRate: number | null;
  /**
   * How many copied trades carry an annotation.
   *
   * ⚠ The point of the whole tab is grading the reps, so "8 of 8" and "1 of 8"
   * are completely different states and the screen says which.
   */
  annotated: number;
}

/** A trade counts as annotated once the user has said ANYTHING about it. */
export function isAnnotated(t: PracticeTradeLike): boolean {
  return Boolean(
    (t.grade && String(t.grade).trim())
    || (t.notes && String(t.notes).trim())
    || (t.patternId && String(t.patternId).trim()),
  );
}

export function summarisePracticeSession(
  trades: readonly PracticeTradeLike[],
): PracticeSessionSummary {
  let net = 0;
  let wins = 0;
  let losses = 0;
  let annotated = 0;

  for (const t of trades) {
    const p = Number(t.netPnl);
    // ⚠ A trade with no P&L is COUNTED but contributes nothing — it is a real
    // rep that has not been filled in, and dropping it would make "3 of 8
    // annotated" read against a denominator of 5.
    if (Number.isFinite(p)) {
      net += p;
      if (p > 0) wins++;
      else if (p < 0) losses++;
    }
    if (isAnnotated(t)) annotated++;
  }

  return {
    n: trades.length,
    net: +net.toFixed(2),
    wins,
    losses,
    winRate: wins + losses ? (wins / (wins + losses)) * 100 : null,
    annotated,
  };
}

/**
 * Sessions newest first.
 *
 * ⚠ By `importedAt`, falling back to the market date. Two imports covering the
 * same market day are two sessions — the plan is explicit — so ordering by the
 * date alone would put them in an arbitrary order and make the newer rep look
 * like the older one.
 */
export function sortPracticeSessions<T extends { marketDate: string; importedAt: string | null }>(
  sessions: readonly T[],
): T[] {
  return [...sessions].sort((a, b) => {
    const ka = a.importedAt ?? a.marketDate;
    const kb = b.importedAt ?? b.marketDate;
    return kb.localeCompare(ka);
  });
}
